import { Component } from "@theme/component";
import { ThemeEvents, BundleChangeEvent } from "@theme/events";
import { formatMoneyWithoutTrailingZeros } from "@theme/money-formatting";
// SLEEPSAKE EDIT — bonus gift-card offer (DEV-327).
// SLEEPSAKE EDIT — bundle total after a percentage tier (DEV-332).
import { bundleTotalAfterDiscount, bundleUnitEach, bundleWithAddOn, packQuantity } from "@theme/bundle-pricing";
// SLEEPSAKE EDIT — the lines one press adds, add-on and card included (DEV-433).
import { buildBundleItems } from "@theme/bundle-items";

/**
 * Custom event fired when a bundle option is selected.
 * Bubbles up to the closest `.shopify-section`; consumers (e.g. sticky add-to-cart bar)
 * should attach their listener to that scope, not document.
 *
 * Lives in this file (rather than alongside the BundleChangeEvent class in
 * `assets/events.js`) so that the typedef sits next to the component that
 * authors / consumes it. Referenced from `events.js` via
 * `import('./bundle-selector').BundleChangeDetail`.
 *
 * @typedef {object} BundleChangeDetail
 * @property {string} sectionId - The section id the bundle belongs to
 * @property {string} blockId - The bundle block id
 * @property {number} qty - Selected quantity (UI selection)
 * @property {number} unitPrice - Single-unit price in cents
 * @property {number} comparePrice - Single-unit compare-at price in cents
 * @property {boolean} hasCompareAt - Whether the variant has a real compare-at price
 * @property {number} discount - Per-option discount percentage
 * @property {boolean} isExtra - Whether this option's discount applies on top of an existing sale (the option's own `is_extra` setting AND the variant has a compare-at price)
 * @property {number} bundlePrice - Final bundle price in cents
 * @property {number} bundleCompare - Bundle compare-at price in cents
 * @property {number} totalDiscountPct - Combined discount percentage off compare-at
 * @property {number} bundleSavingsFloor - Savings amount in cents, floored to whole dollars
 * @property {string} shortLabel - Per-option short label for sticky bar (e.g. "2-Pack")
 */

/**
 * @typedef {object} BundleSelectorRefs
 * @property {Element[]} [discountBadge] - Per-option discount badge `<span>` (only present for options with discount > 0)
 * @property {Element[]} [bundlePrice] - Per-option price display element (parallel to the option order)
 * @property {Element[]} [bundleCompare] - Per-option compare-at wrapper element (parallel to the option order)
 * @property {Element[]} [bundleCompareText] - Per-option compare-at text (`<s>`) element
 * @property {Element[]} [bundleSavings] - Per-option savings wrapper element
 * @property {Element[]} [bundleSavingsText] - Per-option savings text (`<span>`) element
 */

// SLEEPSAKE EDIT — the hero option's CSS animations, named in `_bundles.liquid`.
const HERO_MOTION = new Set(["bundle-option-sheen", "bundle-option-badge-pulse"]);

/**
 * `<bundle-selector>` web component.
 *
 * Reads pricing config from its own `data-*` attributes (set by the `_bundles`
 * block), reads per-option qty/discount from the `<input type="radio">` of each
 * `_bundle-option` sub-block, and on change:
 *
 *   1. Computes the bundle total (handles percentage/flat + extra-discount-mode).
 *   2. Updates the `<product-price>` element in the same section to reflect it.
 *   3. Syncs the qty into the closest product form via both `data-quantity-override`
 *      (consumed by the SLEEPSAKE patch in `assets/product-form.js` for the regular
 *      Add-to-Cart path) AND a hidden `<input name="quantity">` (the canonical form
 *      field that Shopify's dynamic checkout buttons — Shop Pay / Apple Pay /
 *      Google Pay — read directly, since they bypass our submit handler).
 *   4. Updates each option's discount badge text ("Extra X% OFF" in extra mode).
 *   5. Dispatches a `bundle:change` event scoped to the closest `.shopify-section`
 *      so the sticky add-to-cart bar can mirror state without document-level cross-talk.
 *
 * @extends {Component<BundleSelectorRefs>}
 */
class BundleSelector extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Element | Document} */
  #section = document;

  // SLEEPSAKE EDIT START — per-pair colour selection state.
  // `#pairSelections[i]` = variant id chosen for pair i, sized to the LARGEST
  // option qty so switching 1 Pair ↔ 2 Pairs is non-destructive. Pair 2
  // mirrors pair 1 until the buyer touches it apart (`#pairsDivergedByUser`).
  /** @type {string[]} */
  #pairSelections = [];
  #pairsDivergedByUser = false;
  /** Whether the accelerated-checkout container was hidden at SSR (sold-out
   *  product) — if so it must never be un-hidden by the uniform/mixed swap.
   *  Read from the render-time `data-ssr-hidden` stamp, NOT live `hidden`
   *  state: a reconnect while a mixed selection had wallets runtime-hidden
   *  would otherwise latch them hidden forever. */
  #acceleratedHiddenAtSSR = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */

  /** @type {AbortController | undefined} */
  #galleryAbortController;

  /** @type {IntersectionObserver | undefined} */
  #heroMotionObserver;
  // SLEEPSAKE EDIT END

  connectedCallback() {
    super.connectedCallback();

    // Re-create the AbortController on every connect — class-field initialisers
    // only fire once, so after `disconnectedCallback()` aborts the controller,
    // any subsequent connect would re-attach listeners with an already-aborted
    // signal (silent no-op → bundle becomes a dead element on section reconnect).
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#section = this.closest(".shopify-section") || document;

    // Delegated change listener on the host element. The radios live as the
    // first child of each `<label class="_bundle-option">` so any selection
    // change bubbles up to here. Owning the listener directly (rather than
    // routing it through `on:change` on each input) keeps this component
    // self-contained and easier to reason about during debugging.
    this.addEventListener("change", this.#onChange, { signal });

    // SLEEPSAKE EDIT START — per-pair colour selection wiring.
    this.addEventListener("click", this.#onSwatchClick, { signal });
    // SLEEPSAKE EDIT (DEV-433): the add-on's colour chips.
    this.addEventListener("click", this.#onAddonSwatchClick, { signal });

    const maxQty = Math.max(
      1,
      ...Array.from(this.querySelectorAll('input[type="radio"]._bundle-option__input'), (input) =>
        parseInt(/** @type {HTMLElement} */ (input).dataset.qty || "1", 10),
      ),
    );
    const defaultVariantId = this.dataset.defaultVariantId || "";
    this.#pairSelections = Array.from({ length: maxQty }, () => defaultVariantId);

    const accelerated = this.#section.querySelector(".accelerated-checkout-block");
    this.#acceleratedHiddenAtSSR =
      accelerated instanceof HTMLElement ? accelerated.hasAttribute("data-ssr-hidden") : false;

    // SLEEPSAKE EDIT END

    // Re-run bundle math when the parent variant changes, so the headline
    // price keeps reflecting the bundle total instead of the variant default.
    this.#section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    // SSR already paints the correct per-option prices and badge text (the
    // parent block does the math with full extra-mode context) — JS only
    // needs to wire up the form override + headline price for the default
    // option on first load.
    this.#applyInitialSelection();

    // SLEEPSAKE EDIT — the hero option's sheen and badge pulse.
    this.#observeHeroMotion();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = undefined;
    // SLEEPSAKE EDIT — the abort signal covers listeners, not the gallery fetch.
    this.#galleryAbortController?.abort();
    this.#heroMotionObserver?.disconnect();
    this.#heroMotionObserver = undefined;
  }

  /**
   * SLEEPSAKE EDIT: pauses the hero option's endless sheen and badge pulse
   * (`_bundles.liquid`) while the selector is off screen, and resumes them
   * when it scrolls back. Both run on the compositor, so this saves GPU and
   * battery rather than main-thread time.
   *
   * Through the Web Animations API rather than a host attribute and
   * `animation-play-state`: a section morph removes attributes the server did
   * not render, but keeps the elements and their running animations, so the
   * paused state survives it. Only the two named animations are touched, never
   * the card's border/shadow transitions.
   */
  #observeHeroMotion() {
    this.#heroMotionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      for (const animation of this.getAnimations({ subtree: true })) {
        if (!(animation instanceof CSSAnimation) || !HERO_MOTION.has(animation.animationName)) continue;
        if (entry.isIntersecting) animation.play();
        else animation.pause();
      }
    });
    this.#heroMotionObserver.observe(this);
  }

  /**
   * Delegated change handler. Filters non-radio events and ignores the
   * "uncheck" half of a radio-group transition (where the previously-checked
   * input fires its own change event with `checked === false`).
   *
   * @param {Event} event
   */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.type !== "radio") return;
    if (!target.classList.contains("_bundle-option__input")) return;
    if (!target.checked) return;
    this.#handleChange(target);
  };

  /**
   * Re-read the variant's unit + compare prices and re-apply bundle math.
   * Runs after Horizon's `<product-price>` has morphed in the variant's new
   * default price so our override lands last.
   *
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    const detail =
      /** @type {{ resource: { id: number, price: number, compare_at_price: number | null } | null, data: { productId?: string, newProduct?: { id: string | number } } }} */ (
        /** @type {any} */ (event).detail
      );
    if (!detail?.resource) return;

    // On a combined-listing variant change, `event.detail.data.newProduct.id`
    // is the new product. Update our tracked product id so subsequent updates
    // apply correctly instead of bailing on a productId mismatch.
    const newProductId = detail.data?.newProduct?.id;
    if (newProductId) {
      this.dataset.productId = String(newProductId);
    } else if (this.dataset.productId && detail.data?.productId && detail.data.productId !== this.dataset.productId) {
      return;
    }

    const newUnit = Number(detail.resource.price);
    if (Number.isFinite(newUnit)) {
      this.dataset.unitPrice = String(newUnit);
    }
    const newCompare = detail.resource.compare_at_price;
    if (newCompare != null) {
      this.dataset.comparePrice = String(newCompare);
      this.dataset.hasCompareAt = String(newCompare > newUnit);
    } else {
      this.dataset.comparePrice = String(newUnit);
      this.dataset.hasCompareAt = "false";
    }
    // SLEEPSAKE EDIT: the new variant's pack size, from the per-variant map
    // `_bundles.liquid` renders (the event's variant JSON has no metafields).
    this.dataset.packQuantity = String(packQuantity(this.#packQuantities()[String(detail.resource.id ?? "")]));

    // The variant's unit / compare prices changed under us — the SSR-rendered
    // per-option totals and discount badges now point at the previous variant.
    // Repaint everything before re-applying the form-quantity override.
    this.#applyAllOptionPrices();
    this.#applyDiscountBadges();

    // SLEEPSAKE EDIT — a live variant picker changed the product's selected
    // variant, so the per-pair colour state must follow it (otherwise
    // #syncFormSelection would keep writing the stale seed back into
    // input[name=id], silently overriding the picker). Guarded on an actual
    // id change so repeated/echoed events can't loop. Dormant on the
    // pillowcase template (its picker is disabled) — this is for any future
    // template running bundles + a live picker.
    const newVariantId = String(detail.resource.id ?? "");
    if (newVariantId && this.#pairSelections.length > 0 && this.#pairSelections[0] !== newVariantId) {
      this.#pairSelections = this.#pairSelections.map(() => newVariantId);
      this.#pairsDivergedByUser = false;
      this.#paintSwatches();
      this.#syncFormSelection();
    }

    // Apply the form-quantity override for the currently-checked option, but
    // skip touching `<product-price>` and skip dispatching `bundle:change` —
    // Horizon's own `<product-price>` morph already re-painted the headline
    // to the variant's new default, and we don't want to spam consumers with
    // a duplicate change event when only the variant (not the option) changed.
    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="radio"]:checked'));
    if (checked) {
      const qty = parseInt(checked.dataset.qty || "1", 10);
      this.#applyToProductForm(qty);
    }
  };

  /**
   * Recompute and repaint every option's per-card price / compare / savings
   * display. Only used after a `variant:update` event — the parent block's
   * SSR loop emits the correct values on first paint.
   *
   * Ref arrays are populated in DOM order, so index `i` lines up across all
   * the parallel ref arrays AND the radio inputs (every option always emits
   * a price/compare/savings node — only the discount badge is conditional).
   */
  #applyAllOptionPrices() {
    const moneyFormat = this.#parseFormat(this.dataset.moneyFormat);
    const moneyWithCurrencyFormat = this.#parseFormat(this.dataset.moneyWithCurrencyFormat);
    const useCurrency = this.dataset.currencyCodeEnabled === "true";
    const format = useCurrency ? moneyWithCurrencyFormat : moneyFormat;
    const currency = this.dataset.currencyCode || "";

    const inputs = this.querySelectorAll('input[type="radio"]._bundle-option__input');
    const priceRefs = this.refs.bundlePrice ?? [];
    const compareRefs = this.refs.bundleCompare ?? [];
    const compareTextRefs = this.refs.bundleCompareText ?? [];
    const savingsRefs = this.refs.bundleSavings ?? [];
    const savingsTextRefs = this.refs.bundleSavingsText ?? [];
    const unitRefs = this.refs.bundleUnit ?? [];
    const unitTextRefs = this.refs.bundleUnitText ?? [];

    inputs.forEach((input, i) => {
      if (!(input instanceof HTMLInputElement)) return;

      const result = this.#computePricing(input);

      const priceEl = priceRefs[i];
      if (priceEl) priceEl.textContent = formatMoneyWithoutTrailingZeros(result.bundlePrice, format, currency);

      const compareWrapper = /** @type {HTMLElement | undefined} */ (compareRefs[i]);
      const compareTextEl = compareTextRefs[i];
      const showCompare = input.dataset.showCompare !== "false";
      if (compareWrapper && compareTextEl) {
        if (showCompare && result.bundleCompare > result.bundlePrice) {
          compareTextEl.textContent = formatMoneyWithoutTrailingZeros(result.bundleCompare, format, currency);
          compareWrapper.hidden = false;
        } else {
          compareWrapper.hidden = true;
        }
      }

      const savingsWrapper = /** @type {HTMLElement | undefined} */ (savingsRefs[i]);
      const savingsTextEl = savingsTextRefs[i];
      const showSavings = input.dataset.showSavings === "true";
      if (savingsWrapper && savingsTextEl) {
        if (showSavings && result.bundleSavingsFloor > 0) {
          savingsTextEl.textContent = formatMoneyWithoutTrailingZeros(result.bundleSavingsFloor, format, currency);
          savingsWrapper.hidden = false;
        } else {
          savingsWrapper.hidden = true;
        }
      }

      // SLEEPSAKE: per-unit "$X each" line.
      const unitWrapper = /** @type {HTMLElement | undefined} */ (unitRefs[i]);
      const unitTextEl = unitTextRefs[i];
      const showUnitPrice = input.dataset.showUnitPrice === "true";
      if (unitWrapper && unitTextEl) {
        if (showUnitPrice) {
          unitTextEl.textContent = formatMoneyWithoutTrailingZeros(result.bundleUnitEach, format, currency);
          unitWrapper.hidden = false;
        } else {
          unitWrapper.hidden = true;
        }
      }
    });
  }

  /**
   * Delegated click handler for the ADD-ON's colour chips (DEV-433). Separate
   * from `#onSwatchClick` because these chips name a different product's
   * variants — the per-pair machinery owns the page product's only.
   *
   * A sold-out chip is rendered `disabled`, so no click arrives from one.
   *
   * @param {Event} event
   */
  #onAddonSwatchClick = (event) => {
    const chip = event.target instanceof Element ? event.target.closest("[data-addon-swatch]") : null;
    if (!(chip instanceof HTMLElement)) return;
    if (chip.dataset.available === "false") return;
    event.preventDefault();
    const row = chip.closest("[data-bundle-addon]");
    if (!row) return;
    for (const other of row.querySelectorAll("[data-addon-swatch]")) {
      const selected = other === chip;
      other.classList.toggle("_bundle-option__swatch--selected", selected);
      other.setAttribute("aria-pressed", String(selected));
    }
    const name = row.querySelector("[data-addon-name]");
    if (name) name.textContent = chip.dataset.colorName || "";
    const thumb = row.querySelector("._bundle-option__addon-thumb");
    if (thumb instanceof HTMLImageElement && chip.dataset.image) {
      thumb.removeAttribute("srcset");
      thumb.src = chip.dataset.image;
    }
    const input = chip.closest("._bundle-option")?.querySelector("._bundle-option__input");
    if (input instanceof HTMLInputElement) {
      // SLEEPSAKE EDIT (DEV-433) — the chosen colour IS the variant that goes
      // in the box, so the radio's dataset is rewritten before anything reads
      // it: `#syncFormSelection` puts `data-addon-variant` in the add payload
      // and `#computePricing` prices the card from the cents pair.
      input.dataset.addonVariant = chip.dataset.variantId || "";
      if (chip.dataset.price) input.dataset.addonCents = chip.dataset.price;
      if (chip.dataset.comparePrice) input.dataset.addonCompareCents = chip.dataset.comparePrice;

      // The row is visible on an unselected card, so picking a colour there
      // is also picking the option.
      if (!input.checked) {
        input.click();
      } else {
        // Already the selected option: nothing else will re-run the price and
        // the form sync for the new colour, so do it here.
        this.#handleChange(input);
      }
      // A colour can cost a different amount, and `#handleChange` only repaints
      // the headline price — the option CARDS are repainted on a variant
      // update and nowhere else, so their own price, compare and Save would
      // keep quoting the colour the page was rendered with.
      this.#applyAllOptionPrices();
    }
  };

  /**
   * SLEEPSAKE EDIT: `{variantId: packQuantity}` for the variants sold in packs.
   *
   * @returns {Record<string, number>}
   */
  #packQuantities() {
    try {
      return JSON.parse(this.dataset.packQuantities || "{}");
    } catch {
      return {};
    }
  }

  /**
   * Pure pricing computation. Mirrors the per-option Liquid in
   * `_bundle-option.liquid` 1:1 so the SSR price and the JS-recomputed price
   * always match. The `is_extra` decision is per-option and travels on each
   * input as `data-is-extra` (set by the Liquid based on the option's own
   * `is_extra` setting + whether the variant has a compare-at price).
   *
   * SLEEPSAKE (15 Sep, DEV-332): the percentage is taken ONCE on the bundle
   * total and floored to the cent (`bundleTotalAfterDiscount`), the way the
   * app's order-level tier allocates at checkout.
   *
   * @param {HTMLInputElement} input
   * @returns {{bundlePrice: number, bundleCompare: number, isExtra: boolean, totalDiscountPct: number, bundleSavingsFloor: number}}
   */
  #computePricing(input) {
    const unitPrice = parseInt(this.dataset.unitPrice || "0", 10);
    const comparePrice = parseInt(this.dataset.comparePrice || this.dataset.unitPrice || "0", 10);
    const hasCompareAt = this.dataset.hasCompareAt === "true";

    const qty = parseInt(input.dataset.qty || "1", 10);
    const discount = parseFloat(input.dataset.discount || "0");
    const wantExtra = input.dataset.isExtra === "true";
    // SLEEPSAKE: 'fixed' mode charges a merchant-set bundle total (carried on
    // data-fixed-cents); 'percent' keeps the legacy variant-price discount math.
    const pricingMode = input.dataset.pricingMode || "percent";
    const isExtra = wantExtra && hasCompareAt && discount > 0 && pricingMode !== "fixed";

    // When the variant has no compare-at price, comparePrice falls back to
    // unitPrice already — but be explicit so a future refactor of the dataset
    // fallback doesn't silently inflate the bundle compare total.
    const effectiveCompare = hasCompareAt ? comparePrice : unitPrice;
    let bundleCompare = effectiveCompare * qty;
    let bundlePrice;

    if (pricingMode === "fixed") {
      bundlePrice = parseInt(input.dataset.fixedCents || "0", 10);
    } else if (hasCompareAt) {
      if (isExtra) {
        bundlePrice = bundleTotalAfterDiscount(unitPrice, qty, discount);
      } else if (discount > 0) {
        bundlePrice = bundleTotalAfterDiscount(comparePrice, qty, discount);
      } else {
        bundlePrice = unitPrice * qty;
      }
    } else if (discount > 0) {
      bundlePrice = bundleTotalAfterDiscount(unitPrice, qty, discount);
    } else {
      bundlePrice = unitPrice * qty;
    }

    // SLEEPSAKE EDIT: per-item price — over qty x the variant's pack size —
    // floored to match the Liquid `divided_by` (integer division).
    const unitEach = bundleUnitEach(bundlePrice, qty, packQuantity(this.dataset.packQuantity));

    // SLEEPSAKE EDIT (DEV-433): the option's add-on rides at its own price,
    // outside the tier's percentage — 1:1 with `_bundle-option.liquid`, and
    // pinned by `theme-tests/bundle-pricing.test.mjs`. A fixed-price
    // (wholesale) tier names its total outright and takes no add-on, which is
    // also why the Liquid resolves no add-on variant there.
    const addOn = pricingMode === "fixed" ? 0 : parseInt(input.dataset.addonCents || "0", 10);
    const addOnCompare = pricingMode === "fixed" ? 0 : parseInt(input.dataset.addonCompareCents || "0", 10);
    const totals = bundleWithAddOn(bundlePrice, bundleCompare, addOn, addOnCompare);
    bundlePrice = totals.total;
    bundleCompare = totals.compare;
    const bundleSavingsFloor = totals.savingsFloor;

    let totalDiscountPct = 0;
    if (bundleCompare > bundlePrice) {
      totalDiscountPct = Math.round(((bundleCompare - bundlePrice) / bundleCompare) * 100);
    }

    return { bundlePrice, bundleCompare, bundleUnitEach: unitEach, isExtra, totalDiscountPct, bundleSavingsFloor };
  }

  /**
   * Find and trigger the change handler for the currently checked radio so
   * price + form-quantity are synced on first paint.
   *
   * Defensively unchecks any extra `is_default` radios — if the merchant
   * accidentally marked multiple options as default, the browser's "last
   * checked attr in source order wins" rule for radios in a same-named
   * group leaves the wrong one selected. Forcing the FIRST `is_default`
   * to win matches the schema info copy on `_bundle-option`.
   */
  #applyInitialSelection() {
    const allDefaulted = this.querySelectorAll('input[type="radio"]._bundle-option__input:checked');
    if (allDefaulted.length > 1) {
      for (let i = 1; i < allDefaulted.length; i++) {
        /** @type {HTMLInputElement} */ (allDefaulted[i]).checked = false;
      }
    }

    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="radio"]:checked'));
    if (checked) {
      this.#handleChange(checked);
    } else {
      const first = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="radio"]'));
      if (first) {
        first.checked = true;
        this.#handleChange(first);
      }
    }

    // SLEEPSAKE EDIT — repaint chips from the (re)seeded selection state.
    // On first load this is a no-op (SSR painted the same defaults), but on
    // a RECONNECT the fresh seed must overwrite whatever selected-states the
    // DOM carried from before the disconnect.
    this.#paintSwatches();
  }

  /**
   * Re-paints each option's discount badge text. Only used after a
   * `variant:update` event — SSR emits the correct text on first paint
   * since `data-is-extra` is per-option and self-contained. After a
   * variant update, `hasCompareAt` may flip and the "extra" branch may
   * no longer apply, so we re-derive the badge text here.
   *
   * The badge `<span>` is conditionally rendered (only when the option's
   * SSR-time discount > 0), so `discountBadge` refs may be a sparser array
   * than the input list. We walk the refs and look up each badge's parent
   * option to find the corresponding input.
   */
  #applyDiscountBadges() {
    const hasCompareAt = this.dataset.hasCompareAt === "true";
    for (const badge of this.refs.discountBadge ?? []) {
      if (!(badge instanceof HTMLElement)) continue;

      const card = badge.closest("._bundle-option");
      const input = /** @type {HTMLInputElement | null} */ (card?.querySelector("._bundle-option__input") ?? null);
      if (!input) continue;

      // SLEEPSAKE: fixed mode derives the badge "% OFF" from the saving vs compare-at.
      if ((input.dataset.pricingMode || "percent") === "fixed") {
        const { bundlePrice, bundleCompare } = this.#computePricing(input);
        if (bundleCompare > bundlePrice) {
          badge.textContent = `${Math.round(((bundleCompare - bundlePrice) / bundleCompare) * 100)}% OFF`;
          badge.hidden = false;
        } else {
          badge.textContent = "";
          badge.hidden = true;
        }
        continue;
      }

      const discount = parseFloat(input.dataset.discount || "0");
      if (discount <= 0) {
        badge.textContent = "";
        badge.hidden = true;
        continue;
      }
      badge.hidden = false;

      const wantExtra = input.dataset.isExtra === "true";
      if (wantExtra && hasCompareAt) {
        badge.textContent = `Extra ${discount}% OFF`;
      } else {
        badge.textContent = `${discount}% OFF`;
      }
    }
  }

  /**
   * Build a `BundleChangeDetail` object for an option input + the host's
   * current dataset. Pure — does not mutate DOM or dispatch events. Shared
   * between `#handleChange` (the on-click path) and the public
   * `getCurrentDetail()` (the consumer-derived-state path) so the two stay
   * in lockstep.
   *
   * @param {HTMLInputElement} input
   * @returns {BundleChangeDetail}
   */
  #buildDetail(input) {
    const qty = parseInt(input.dataset.qty || "1", 10);
    const { bundlePrice, bundleCompare, isExtra, totalDiscountPct, bundleSavingsFloor } = this.#computePricing(input);

    return {
      sectionId: this.dataset.sectionId || "",
      blockId: this.dataset.blockId || "",
      qty,
      unitPrice: parseInt(this.dataset.unitPrice || "0", 10),
      comparePrice: parseInt(this.dataset.comparePrice || this.dataset.unitPrice || "0", 10),
      hasCompareAt: this.dataset.hasCompareAt === "true",
      discount: parseFloat(input.dataset.discount || "0"),
      isExtra,
      bundlePrice,
      bundleCompare,
      totalDiscountPct,
      bundleSavingsFloor,
      shortLabel: input.dataset.shortLabel || "",
    };
  }

  /**
   * Public: returns the bundle change detail for the currently-checked
   * option, recomputed from the host's live dataset. Consumers like the
   * sticky add-to-cart bar use this to derive an initial / post-variant-
   * update state without depending on having received a `bundle:change`
   * event AND without having to duplicate the pricing math.
   *
   * @returns {BundleChangeDetail | null}
   */
  getCurrentDetail() {
    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]._bundle-option__input:checked')
    );
    if (!checked) return null;
    return this.#buildDetail(checked);
  }

  /**
   * @param {HTMLInputElement} input
   */
  #handleChange(input) {
    const detail = this.#buildDetail(input);

    this.#applyToProductForm(detail.qty);
    this.#applyToProductPrice(detail.bundlePrice, detail.bundleCompare);
    // SLEEPSAKE EDIT — re-derive variant id + uniform/mixed form state for the
    // newly selected option's qty (pair selections persist across options).
    this.#syncFormSelection();
    // SLEEPSAKE EDIT — runs AFTER #syncFormSelection so the gift offer's
    // wallet swap is the last writer: a live offer disqualifies the wallet
    // widgets regardless of whether the colour selection is uniform.
    this.#applyGiftOffer(input);

    this.#section.dispatchEvent(new BundleChangeEvent(detail));
  }

  // SLEEPSAKE EDIT START — per-pair colour selection.

  /**
   * Delegated click handler for the per-pair colour chips. Chips are only
   * visible inside the checked card (unchecked cards collapse their rows via
   * CSS), so in practice clicks always come from the active card — the
   * check-the-radio branch below is defence-in-depth in case a future layout
   * exposes chips on collapsed cards.
   *
   * @param {Event} event
   */
  #onSwatchClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const swatch = target?.closest?.("[data-bundle-swatch]");
    // Sold-out chips are deliberately selectable (browse parity with the
    // variant picker) — the purchase gate lives in #syncFormSelection, so
    // no availability check belongs here.
    if (!(swatch instanceof HTMLButtonElement)) return;

    const row = swatch.closest("[data-pair-row]");
    const card = swatch.closest("._bundle-option");
    if (!(row instanceof HTMLElement) || !card) return;

    const pairIndex = parseInt(row.dataset.pairIndex || "0", 10);
    const variantId = swatch.dataset.variantId || "";
    if (!variantId) return;

    const previous = this.#pairSelections[pairIndex];
    this.#pairSelections[pairIndex] = variantId;

    if (pairIndex === 0 && !this.#pairsDivergedByUser) {
      // Pair 2..N follow pair 1 until the buyer deliberately splits them.
      for (let i = 1; i < this.#pairSelections.length; i++) {
        if (this.#pairSelections[i] === previous) this.#pairSelections[i] = variantId;
      }
    } else if (pairIndex > 0 && variantId !== this.#pairSelections[0]) {
      this.#pairsDivergedByUser = true;
    }

    // Selecting a colour in a non-selected card selects that card too. The
    // radio's change event then runs #handleChange → #syncFormSelection, so
    // skip the direct sync on that path to avoid doing the work twice.
    const radio = /** @type {HTMLInputElement | null} */ (card.querySelector("._bundle-option__input"));
    if (radio && !radio.checked) {
      radio.checked = true;
      this.#handleChange(radio);
    } else {
      this.#syncFormSelection();
    }
    this.#paintSwatches();

    // The gallery follows Pair 1's colour (pair 2+ changes don't move it —
    // there's one gallery and the first pair is the "primary" choice).
    if (pairIndex === 0 && variantId !== previous) {
      this.#refreshGalleryMedia(variantId);
    }
  };

  /**
   * Swap the section's media gallery for the given variant's server-rendered
   * one. A client-side scroll can't do this: the gallery block's
   * `hide_variants` filter excludes other variants' media from the SSR DOM
   * entirely, so the target slide doesn't exist until the section is
   * re-rendered with `?variant=`. Only `<media-gallery>` is replaced — same
   * swap the component itself performs on `variant:update` — rather than
   * dispatching a full VariantUpdateEvent, which would also morph
   * buy-buttons/price and clobber the uniform/mixed wallet-swap state this
   * component owns. Failures (network, aborted by a newer click) keep the
   * current gallery.
   *
   * @param {string} variantId
   */
  #refreshGalleryMedia(variantId) {
    const gallery = this.#section.querySelector("media-gallery");
    const productUrl = this.dataset.productUrl;
    const sectionId = this.dataset.sectionId;
    if (!gallery || !productUrl || !sectionId) return;

    this.#galleryAbortController?.abort();
    this.#galleryAbortController = new AbortController();

    fetch(`${productUrl}?variant=${variantId}&section_id=${sectionId}`, {
      signal: this.#galleryAbortController.signal,
    })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`))))
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const newGallery = html.querySelector("media-gallery");
        const currentGallery = this.#section.querySelector("media-gallery");
        if (newGallery && currentGallery) currentGallery.replaceWith(newGallery);
      })
      .catch(() => {});
  }

  /**
   * Repaint every card's chip selected-state + per-row colour name from
   * `#pairSelections`. All cards are painted (not just the checked one) so
   * state survives switching options in either direction.
   */
  #paintSwatches() {
    for (const row of this.querySelectorAll("[data-pair-row]")) {
      if (!(row instanceof HTMLElement)) continue;
      const pairIndex = parseInt(row.dataset.pairIndex || "0", 10);
      const selectedId = this.#pairSelections[pairIndex];
      let selectedName = "";
      for (const swatch of row.querySelectorAll("[data-bundle-swatch]")) {
        if (!(swatch instanceof HTMLElement)) continue;
        const isSelected = swatch.dataset.variantId === selectedId;
        swatch.classList.toggle("_bundle-option__swatch--selected", isSelected);
        swatch.setAttribute("aria-pressed", String(isSelected));
        if (isSelected) selectedName = swatch.dataset.colorName || "";
      }
      const nameEl = row.querySelector("[data-pair-name]");
      if (nameEl && selectedName) nameEl.textContent = selectedName;
    }
  }

  /**
   * Derive the active selection (checked option's qty ✕ pair variant ids) and
   * sync it into the product form + swap the express-checkout affordance:
   *
   *   UNIFORM (all pairs same variant, or single-pair): the form fully
   *   represents the purchase — `<input name="id">` = the variant, hidden
   *   quantity = qty — so Shopify's dynamic checkout buttons keep working
   *   and `data-bundle-items` is cleared (regular ATC path untouched).
   *
   *   MULTI-LINE (pairs span variants, or the option carries an add-on
   *   product): no single-variant form can represent two line items and the
   *   wallet widgets read only the form, so the accelerated-checkout container
   *   is hidden and nothing stands in for it — Ali, 19 Sep: the theme offers
   *   Shopify's own express buttons or none, never a Buy now of its own.
   *   `data-bundle-items` carries the line items consumed by the SLEEPSAKE
   *   patch in product-form.js (regular ATC).
   *
   * SLEEPSAKE EDIT (DEV-433) — the add-on is the second reason to be
   * multi-line, and it exists on a single-variant product (the pillow), so
   * this no longer returns early for one.
   */
  #syncFormSelection() {
    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]._bundle-option__input:checked')
    );
    if (!checked) return;

    // SLEEPSAKE EDIT (DEV-433) — the seed check used to come first and skip
    // the whole method. It still guards the colour machinery, but an option
    // carrying an add-on has a second line to describe whether or not the
    // product has colours at all, so the method now runs for it too.
    const addonVariantId = checked.dataset.addonVariant || "";
    const seeded = this.#pairSelections.length > 0 && !!this.#pairSelections[0];
    if (!seeded && !addonVariantId) return;

    const qty = parseInt(checked.dataset.qty || "1", 10);
    const active = this.#pairSelections.slice(0, qty);
    // Sold-out chips are selectable (browse parity with the variant picker),
    // so the purchase gate lives here: any unavailable variant in the active
    // selection disables the submit and hides both express paths.
    const allAvailable = active.every(
      (id) => this.querySelector(`[data-bundle-swatch][data-variant-id="${id}"]`)?.dataset.available !== "false",
    );

    // SLEEPSAKE EDIT (DEV-433) — one builder for both reasons a selection
    // needs more than one line: colours that span variants, and an option that
    // carries an add-on product. More than one line is exactly the condition
    // the wallet widgets cannot express, so it decides the express swap below
    // too (it is `!uniform` on every pre-DEV-433 selection).
    let lineVariantIds = active.filter(Boolean);
    if (lineVariantIds.length === 0 && addonVariantId) {
      // Unreachable while `data-default-variant-id` is stamped, but silence
      // here would mean an add of pillowcases with no pillow, or (once the
      // dataset is dropped below) a hero press that quietly adds neither. Take
      // the variant the form itself is carrying and say so.
      const formId = /** @type {HTMLInputElement | null} */ (
        this.#section.querySelector('product-form-component form[data-type="add-to-cart-form"] input[name="id"]')
      );
      const fallbackId = formId?.value || this.dataset.defaultVariantId || "";
      console.warn("bundle-selector: no pair selection for an add-on option; falling back to the form variant");
      if (fallbackId) lineVariantIds = Array.from({ length: qty }, () => fallbackId);
    }

    const items = buildBundleItems(lineVariantIds, addonVariantId);
    const multiLine = items.length > 1;

    const forms = this.#section.querySelectorAll('product-form-component form[data-type="add-to-cart-form"]');
    for (const form of forms) {
      if (!(form instanceof HTMLFormElement)) continue;

      const idInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'));
      if (idInput && active[0]) idInput.value = active[0];

      this.#applySubmitAvailability(form, allAvailable);

      if (multiLine) {
        form.dataset.bundleItems = JSON.stringify(items);
      } else {
        delete form.dataset.bundleItems;
      }
    }

    const accelerated = this.#section.querySelector(".accelerated-checkout-block");
    // The SSR latch only holds when NOTHING on the page is buyable. A
    // deep-link to a sold-out variant (?variant=) also SSR-hides the wallets
    // even though a sibling colour is in stock — that case is ours to manage.
    const ssrLatched = this.#acceleratedHiddenAtSSR && !this.#anyChipAvailable();
    // SLEEPSAKE EDIT — a wallet block hidden at render time BECAUSE the bonus
    // gift-card offer is live must stay hidden: the wallets would check the
    // buyer out without the free card no matter how uniform the selection is.
    const bonusLatched = accelerated instanceof HTMLElement && accelerated.hasAttribute("data-bonus-gift-hidden");
    if (accelerated instanceof HTMLElement && !ssrLatched && !bonusLatched) {
      accelerated.hidden = multiLine || !allAvailable;
    }
    this.#applyStickyAvailability(allAvailable);
  }

  /**
   * Publish the selected tier's bonus gift-card state to the product form.
   *
   * Three values, because they answer three different questions:
   *
   *   `data-gift-offer`  does the offer apply to THIS tier? A fixed-price
   *   (wholesale) tier answers no, and clearing it here is what stops a
   *   fixed-price tier on a template with no `wholesale` suffix from stamping
   *   a retail claim onto a trade line. A tier whose denomination is merely
   *   missing — the kill switch — still answers yes, so dropping to one unit
   *   later still earns the card.
   *
   *   `data-gift-variant` / `data-gift-variant-double`  the two denominations.
   *   `assets/product-form.js` picks between them by the quantity being added.
   *
   * The dataset is the same channel `data-quantity-override` uses: the
   * SLEEPSAKE patch in `assets/product-form.js` reads these at submit time and
   * adds the product and the card in ONE request, so the drawer renders once.
   *
   * Nothing here touches the express-checkout affordances. While the offer is
   * live the wallets are hidden by `blocks/accelerated-checkout.liquid` at
   * render time and nothing un-hides them (`#syncFormSelection` honours the
   * `data-bonus-gift-hidden` latch). Ali's call is that an eligible product
   * page then offers one CTA, Add to cart — no exceptions.
   *
   * @param {HTMLInputElement} input - The newly checked option radio.
   */
  #applyGiftOffer(input) {
    const fixedPricing = input.dataset.pricingMode === "fixed";
    const offerLive = !fixedPricing && input.dataset.giftOffer === "1";

    const forms = this.#section.querySelectorAll('product-form-component form[data-type="add-to-cart-form"]');
    for (const form of forms) {
      if (!(form instanceof HTMLFormElement)) continue;

      if (offerLive) {
        form.dataset.giftOffer = "1";
      } else {
        delete form.dataset.giftOffer;
      }

      this.#setFormGiftVariant(form, "giftVariant", offerLive ? input.dataset.giftVariant : "");
      this.#setFormGiftVariant(form, "giftVariantDouble", offerLive ? input.dataset.giftVariantDouble : "");
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} key
   * @param {string | undefined} value
   */
  #setFormGiftVariant(form, key, value) {
    if (value) {
      form.dataset[key] = value;
    } else {
      delete form.dataset[key];
    }
  }

  /** Chip availability is SSR-static; queried lazily so the gate and the
   *  wallet latch agree at any point in the element's lifecycle. */
  #anyChipAvailable() {
    return !!this.querySelector('[data-bundle-swatch][data-available="true"]');
  }

  /**
   * Gate a form's submit button on the selection's availability. The label
   * element is the inner text span of `snippets/add-to-cart-button.liquid`;
   * the enabled-state label for deep-link adoption is stamped by
   * `blocks/add-to-cart.liquid` as `data-bundle-available-label`.
   *
   * @param {HTMLFormElement} form
   * @param {boolean} allAvailable
   */
  #applySubmitAvailability(form, allAvailable) {
    const submit = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[name="add"]'));
    if (!submit) return;
    const label = /** @type {HTMLElement | null} */ (submit.querySelector(".add-to-cart-text__content span span"));
    const stamp = /** @type {HTMLElement | null} */ (form.querySelector("[data-bundle-available-label]"));
    this.#applyCtaAvailability(submit, label, allAvailable, stamp?.dataset.bundleAvailableLabel);
  }

  /**
   * Keep the sticky bar honest about the selection: its visibility CSS keys
   * on `data-variant-available` (see `sections/product-information.liquid`),
   * and its button is a puppet that clicks the main submit — left enabled it
   * plays the full "added" success animation against a disabled target and
   * permanently poisons the puppet flag, so it is gated in lockstep. Its
   * label span is a DIRECT child of the content span (the quantity chip is
   * the sibling), hence the different selector from the main button.
   *
   * @param {boolean} allAvailable
   */
  #applyStickyAvailability(allAvailable) {
    // No chips = no availability information: `allAvailable` degenerates to
    // true on chip-less bundled products (e.g. single-variant templates
    // running _bundles + sticky), and writing it would force-show a bar the
    // theme SSR-hides for a sold-out product. Same predicate as the wallet
    // latch.
    if (!this.querySelector("[data-bundle-swatch]")) return;
    const sticky = this.#section.querySelector("sticky-add-to-cart");
    if (!(sticky instanceof HTMLElement)) return;
    sticky.setAttribute("data-variant-available", String(allAvailable));

    const button = /** @type {HTMLButtonElement | null} */ (sticky.querySelector("button.sticky-add-to-cart__button"));
    if (!button) return;
    const label = /** @type {HTMLElement | null} */ (button.querySelector(".add-to-cart-text__content > span"));
    this.#applyCtaAvailability(button, label, allAvailable, button.dataset.bundleAvailableLabel);
  }

  /**
   * Disable/re-enable one CTA for a sold-out selection, swapping its label
   * to the localized "Sold out" and back. Only a disable applied here is
   * ever undone (`data-bundle-soldout` marker) — with one deliberate
   * exception: a button that is SSR-disabled because the page's `?variant=`
   * deep-link points at a sold-out colour is ADOPTED (marker + the
   * SSR-stamped enabled-state label as the stash) when a sibling colour is
   * buyable, so picking that sibling can hand the button back. Without an
   * available sibling or a label stamp the SSR disable stays permanent.
   *
   * @param {HTMLButtonElement} button
   * @param {HTMLElement | null} label
   * @param {boolean} allAvailable
   * @param {string} [availLabel]
   */
  #applyCtaAvailability(button, label, allAvailable, availLabel) {
    const icon = /** @type {HTMLElement | null} */ (button.querySelector(".add-to-cart-icon"));

    if (!allAvailable) {
      if (button.disabled) {
        if (!button.dataset.bundleSoldout && availLabel && this.#anyChipAvailable()) {
          button.dataset.bundleSoldout = "1";
          button.dataset.bundleOrigLabel = availLabel;
        }
        return;
      }
      button.dataset.bundleSoldout = "1";
      button.disabled = true;
      if (label) {
        button.dataset.bundleOrigLabel = label.textContent || "";
        label.textContent = this.dataset.soldoutText || "Sold out";
      }
      if (icon) icon.hidden = true;
    } else if (button.dataset.bundleSoldout) {
      delete button.dataset.bundleSoldout;
      button.disabled = false;
      if (label && button.dataset.bundleOrigLabel != null) {
        label.textContent = button.dataset.bundleOrigLabel;
        delete button.dataset.bundleOrigLabel;
      }
      if (icon) icon.hidden = false;
    }
  }
  // SLEEPSAKE EDIT END

  /**
   * Syncs the bundle qty into the closest product form via two complementary
   * channels:
   *
   *   1. `form.dataset.quantityOverride = qty` — consumed by the SLEEPSAKE
   *      patch in `assets/product-form.js` for the regular Add-to-Cart submit
   *      path (also clamps to variant min/max/step).
   *   2. `<input name="quantity">` — the form's actual quantity field. Required
   *      for Shopify's dynamic checkout buttons (Shop Pay, Apple Pay, Google
   *      Pay) which are rendered by `{{ form | payment_button }}` and BYPASS
   *      `<product-form-component>`'s submit handler entirely. Those widgets
   *      read the form's literal field values, so the dataset override is
   *      invisible to them. If the qty block is disabled in the template (the
   *      typical bundle setup, where `quantity_selector` is intentionally
   *      hidden), no such input exists at SSR time and we create+append a
   *      hidden one tagged `data-bundle-managed` so the next bundle change
   *      reuses it instead of stacking duplicates.
   *
   * @param {number} qty
   */
  #applyToProductForm(qty) {
    const forms = this.#section.querySelectorAll('product-form-component form[data-type="add-to-cart-form"]');
    for (const form of forms) {
      if (!(form instanceof HTMLFormElement)) continue;

      form.dataset.quantityOverride = String(qty);

      let qtyInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="quantity"]'));
      if (!qtyInput) {
        qtyInput = document.createElement("input");
        qtyInput.type = "hidden";
        qtyInput.name = "quantity";
        qtyInput.dataset.bundleManaged = "";
        form.appendChild(qtyInput);
      }
      qtyInput.value = String(qty);
    }
  }

  /**
   * Updates the `<product-price>` block in the same section to display the
   * bundle total / compare. Toggles between Horizon's `.price__regular` and
   * `.price__sale` containers via the shared `.price__hidden` modifier.
   *
   * @param {number} bundlePrice
   * @param {number} bundleCompare
   */
  #applyToProductPrice(bundlePrice, bundleCompare) {
    const moneyFormat = this.#parseFormat(this.dataset.moneyFormat);
    const moneyWithCurrencyFormat = this.#parseFormat(this.dataset.moneyWithCurrencyFormat);
    const useCurrency = this.dataset.currencyCodeEnabled === "true";
    const format = useCurrency ? moneyWithCurrencyFormat : moneyFormat;
    const currency = this.dataset.currencyCode || "";

    const formattedPrice = formatMoneyWithoutTrailingZeros(bundlePrice, format, currency);
    const formattedCompare = formatMoneyWithoutTrailingZeros(bundleCompare, format, currency);
    const onSale = bundleCompare > bundlePrice;

    for (const productPrice of this.#section.querySelectorAll("product-price")) {
      // Volume-pricing branch in `snippets/price.liquid` uses a different DOM
      // tree (no `.price__regular` / `.price__sale`) — bundle math + volume
      // pricing are mutually exclusive concepts, so skip the repaint and
      // leave the headline showing the volume-pricing range.
      const hasVolumeBranch = !productPrice.querySelector(".price__regular, .price__sale");
      if (hasVolumeBranch) {
        console.warn("[bundle-selector] product variant uses volume pricing; bundle headline price repaint skipped");
        continue;
      }

      const regular = productPrice.querySelector(".price__regular");
      const sale = productPrice.querySelector(".price__sale");

      if (onSale) {
        regular?.classList.add("price__hidden");
        sale?.classList.remove("price__hidden");
        const saleSpan = productPrice.querySelector(".price__sale .price-item--sale");
        if (saleSpan) saleSpan.textContent = formattedPrice;
        const compareSpan = productPrice.querySelector(".price__sale .price-item--regular.compare-at-price");
        if (compareSpan) compareSpan.textContent = formattedCompare;
      } else {
        sale?.classList.add("price__hidden");
        regular?.classList.remove("price__hidden");
        const regularSpan = productPrice.querySelector(".price__regular .price");
        if (regularSpan) regularSpan.textContent = formattedPrice;
      }
    }
  }

  /**
   * @param {string | undefined} raw
   * @returns {string}
   */
  #parseFormat(raw) {
    return raw && raw.length > 0 ? raw : "${{amount}}";
  }
}

if (!customElements.get("bundle-selector")) {
  customElements.define("bundle-selector", BundleSelector);
}
